import { api, queryString, type Items, type Paginated } from '@sentinel/web-core';
import type { MISSION_SORTS } from '@sentinel/shared';
import type {
  Alarm,
  DeviceAssignment,
  FieldCredential,
  MissionDashboard,
  MissionClose,
  MissionCreate,
  MissionEvent,
  MissionFacets,
  MissionSummary,
  MissionUpdate,
  Pairing,
  PairingCreate,
  State,
} from '@sentinel/shared';

export interface PersonRow {
  id: string;
  code: string | null;
  displayName: string;
  roleTitle: string;
  homeOrganization: string;
  kind: string;
  state: State;
  teamLabel: string | null;
  deviceLabel: string | null;
  trackerLabel: string | null;
  hasAccount: boolean;
}

export const missionEndpoints = {
  /** Filtered, sorted and paged by the server (CLAUDE.md §24): `levels` a comma list. */
  list: (params: {
    status?: 'open' | 'closed' | 'all';
    page?: number;
    pageSize?: number;
    q?: string;
    levels?: string;
    period?: string;
    sort?: (typeof MISSION_SORTS)[number];
    order?: 'asc' | 'desc';
  }) => api<Paginated<MissionSummary>>(`/missions${queryString(params)}`),
  facets: () => api<MissionFacets>('/missions/facets'),
  get: (id: string) => api<MissionSummary>(`/missions/${id}`),
  dashboard: (id: string) => api<MissionDashboard>(`/missions/${id}/dashboard`),
  events: (id: string, params: { kind?: string; page?: number; pageSize?: number }) =>
    api<Paginated<MissionEvent>>(`/missions/${id}/events${queryString(params)}`),
  addEvent: (id: string, body: { kind?: string; text: string; subjectCode?: string }) =>
    api<MissionEvent>(`/missions/${id}/events`, { method: 'POST', body }),
  alarms: (id: string) => api<Items<Alarm>>(`/missions/${id}/alarms`),
  acknowledgeAlarm: (missionId: string, alarmId: string) =>
    api<Alarm>(`/missions/${missionId}/alarms/${alarmId}/acknowledge`, { method: 'POST' }),
  closeAlarm: (missionId: string, alarmId: string) =>
    api<Alarm>(`/missions/${missionId}/alarms/${alarmId}/close`, { method: 'POST' }),
  open: (body: MissionCreate) => api<MissionSummary>('/missions', { method: 'POST', body }),
  update: (id: string, body: MissionUpdate) =>
    api<MissionSummary>(`/missions/${id}`, { method: 'PATCH', body }),
  close: (id: string, body: MissionClose) =>
    api<MissionSummary>(`/missions/${id}/close`, { method: 'POST', body }),
  teams: (id: string) => api<Items<MissionDashboard['teams'][number]>>(`/missions/${id}/teams`),
  persons: (id: string) => api<Items<PersonRow>>(`/missions/${id}/persons`),
  resources: (id: string) =>
    api<Items<MissionDashboard['resources'][number]>>(`/missions/${id}/resources`),
  /** Resources of the organization not engaged in an open operation. */
  availableResources: () =>
    api<Items<MissionDashboard['resources'][number]>>('/resources/available'),
  assignResource: (id: string, resourceId: string) =>
    api<MissionDashboard['resources'][number]>(`/missions/${id}/resources/${resourceId}`, {
      method: 'PUT',
    }),
  releaseResource: (id: string, resourceId: string) =>
    api<{ status: 'ok' }>(`/missions/${id}/resources/${resourceId}`, { method: 'DELETE' }),
  devices: (id: string) =>
    api<Items<MissionDashboard['devices'][number]>>(`/missions/${id}/devices`),
  /** A LoRa node or gateway given to a team, a resource or a person; null takes it back. */
  assignDevice: (id: string, deviceId: string, body: DeviceAssignment) =>
    api<undefined>(`/missions/${id}/devices/${deviceId}/assignment`, { method: 'PUT', body }),
  pairing: (id: string, body: PairingCreate) =>
    api<Pairing>(`/missions/${id}/pairings`, { method: 'POST', body }),
  /** Sentinel Field (ADR 0029): the phones paired to the mission, and the revocation of one. */
  fieldPhones: (id: string) => api<Items<FieldCredential>>(`/missions/${id}/field-phones`),
  revokeFieldPhone: (id: string, phoneId: string) =>
    api<undefined>(`/missions/${id}/field-phones/${phoneId}`, { method: 'DELETE' }),
};
